import React from "react";
import { useRecoilState } from "recoil";
import { FRANCHISES } from "config";
import t from "lib/i18n";
import { noop } from "lib/utils";
import { getRecentStations } from "lib/recentStations";
import useConfig from "hooks/useConfig";
import useLocation from "hooks/useLocation";
import useRoveFocus from "hooks/useRoveFocus";
import { getStationsFromLatLng } from "services/StationsService";
import { locationErrorState, nearestStationsState } from "state";
import ErrorMessage from "components/ui/ErrorMessage";
import ListItem from "components/ListItem";
import { ReactComponent as LocationIcon } from "static/images/location.svg";
import "styles/components/stations-list.scss";

const ERROR_VISIBLE_TIME_MS = 10000;

const PopularStations = {
  [FRANCHISES.none]: [],
  [FRANCHISES.southeastern]: [
    { name: "Canterbury West", nlc: "5007", crs: "CBW" },
    { name: "London Terminals", nlc: "1072", crs: "" },
    { name: "Margate", nlc: "5018", crs: "MAR" },
    { name: "Tonbridge", nlc: "5229", crs: "TON" },
    { name: "Tunbridge Wells", nlc: "5230", crs: "TBW" }
  ],
  [FRANCHISES.gtr]: [
    { name: "Gatwick Airport", nlc: "5416", crs: "GTW" },
    { name: "London Victoria", nlc: "5426", crs: "VIC" },
    { name: "Brighton", nlc: "5268", crs: "BTN" },
    { name: "London Bridge", nlc: "5148", crs: "LBG" },
    { name: "London St Pancras", nlc: "1555", crs: "STP" },
  ],
};

const StationsList = ({
  hideList,
  name,
  location,
  onLocationError = noop,
  onLocationFound = noop,
  onLocationStart = noop,
  setValue,
  arrowNavigation,
  disableGeolocation = false,
  disablePopularStations = false,
  ticketType
}) => {
  const { apiUrl, popularStationsList } = useConfig();
  const recentStations = getRecentStations(name, ticketType);
  const [nearestStations, setNearestStations] = useRecoilState(nearestStationsState); // retrieve the form's state from a global state
  const [locationError, setLocationError] = useRecoilState(locationErrorState);

  const BrandPopularStations = PopularStations[popularStationsList] ?? [];

  const nearestStationsLength = location && !disableGeolocation ? nearestStations.length + 1 : 0;
  const allStationsLength =
    nearestStationsLength +
    recentStations.length +
    (!disablePopularStations ? BrandPopularStations.length : 0);

  const [focus, setFocus] = useRoveFocus(allStationsLength);

  const setTimeoutedLocationError = error => {
    onLocationError(error);
    setLocationError(error);
    setTimeout(() => setLocationError?.(null), ERROR_VISIBLE_TIME_MS);
  };

  const onUseLocationSuccess = async ({ latitude, longitude, position }) => {
    onLocationStart();
    setLocationError(null);
    const results = await getStationsFromLatLng({ latitude, longitude }, apiUrl);
    if (results.length === 0) {
      setTimeoutedLocationError(t("location.error.noResults"));
    } else {
      setNearestStations(results);
      onLocationFound();
    }
  };

  const onUseLocationError = errorCode => {
    setTimeoutedLocationError(t(errorCode));
  };

  const onUseLocationPermissionChange = state => {};

  // TODO move higher to StationAutosuggest so it's not re-rendered
  const { getCurrentPosition, isLocationSupported } = useLocation(
    onUseLocationSuccess,
    onUseLocationError,
    onUseLocationPermissionChange
  );

  const onRequestLocation = () => {
    getCurrentPosition();
  };

  return (
    <div className="otrl-jp__stations-list">
      {isLocationSupported && location && !disableGeolocation && (
        <div>
          <h3 className="otrl-jp__stations-list__heading">Nearest stations</h3>
          <ul
            className="otrl-jp__stations-list__list"
            role="navigation"
            aria-label="Nearest stations list"
          >
            <ListItem
              className="otrl-jp__stations-list__list__item"
              setFocus={setFocus}
              index={0}
              focus={focus === 0 && arrowNavigation}
              onSelect={onRequestLocation}
              onTab={hideList}
            >
              <LocationIcon className="otrl-jp__stations-list__list__item__icon" />
              Use my current location
            </ListItem>
            {locationError && <ErrorMessage message={locationError} noBorder />}
            {nearestStations.length > 0 &&
              nearestStations.map((station, index) => (
                <ListItem
                  key={index}
                  className="otrl-jp__stations-list__list__item"
                  setFocus={setFocus}
                  index={index + 1}
                  focus={focus === index + 1 && arrowNavigation}
                  onSelect={() => {
                    setValue(station);
                    hideList();
                  }}
                  onTab={hideList}
                >
                  {station.name}
                </ListItem>
              ))}
          </ul>
        </div>
      )}
      <div>
        <h3 className="otrl-jp__stations-list__heading">Recent</h3>
        <ul
          className="otrl-jp__stations-list__list"
          role="navigation"
          aria-label="Recent stations list"
        >
          {recentStations.length ? (
            recentStations.map((station, index) => (
              <ListItem
                key={index}
                className="otrl-jp__stations-list__list__item"
                setFocus={setFocus}
                index={index + nearestStationsLength}
                focus={focus === index + nearestStationsLength && arrowNavigation}
                onSelect={() => {
                  setValue(station);
                  hideList();
                }}
                onTab={hideList}
              >
                {station.name}
              </ListItem>
            ))
          ) : (
            <li className="otrl-jp__stations-list__list__item">No recent searches</li>
          )}
        </ul>
      </div>
      {!disablePopularStations && BrandPopularStations.length ? (
        <div>
          <h3 className="otrl-jp__stations-list__heading">Popular</h3>
          <ul
            className="otrl-jp__stations-list__list"
            role="navigation"
            aria-label="Popular stations list"
          >
            {BrandPopularStations.map((station, index) => (
              <ListItem
                key={index}
                className="otrl-jp__stations-list__list__item"
                setFocus={setFocus}
                index={index + recentStations.length + nearestStationsLength}
                focus={
                  focus === index + recentStations.length + nearestStationsLength && arrowNavigation
                }
                onSelect={() => {
                  setValue(station);
                  hideList();
                }}
                onTab={hideList}
              >
                {station.name}
              </ListItem>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export default StationsList;
